import classNames from 'classnames';
import lodash from 'lodash';
import type { CreateMediaUrl } from 'pro-gallery';
import { GALLERY_CONSTS, ProGallery } from 'pro-gallery';
import type { Settings } from 'pro-gallery-lib';
import type { FC } from 'react';
import React, { useContext, useMemo, useRef } from 'react';

import { LayoutFixer } from './../../bundled-dependencies/pro-gallery-layout-fixer';
import { RicosContext } from './../../ricos-context';
import type { GalleryData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type {
  AnchorTarget,
  Helpers,
  LegacyEditorPluginConfig,
  RelValue,
  SEOSettings,
} from './../../ricos-types';
import { resizeMediaUrl } from './../../_shared/media/resize-media-url';

import ExpandIcon from '.././icons/expand';
import { GALLERY_TYPE } from '.././types';
import { ROOT } from '../data-hooks';
import classes from '../statics/styles/viewer.rtlignore-scss';
import { isValidGalleryId } from './is-valid-gallery-id';
import { useHeight } from './use-height';
import { useItems } from './useItems';
import { useOptions } from './useOptions';
import { isHorizontalLayout, sampleItems } from './utils';

const GALLERY_EVENTS = GALLERY_CONSTS.events;

type GalleryViewerProps = {
  componentData: GalleryData;
  nodeId: string;
  settings: LegacyEditorPluginConfig;
  relValue?: RelValue;
  anchorTarget?: AnchorTarget;
  seoMode?: SEOSettings;
  helpers?: Helpers;
  itemOverlayElement?: ({ url }: { url: string }) => JSX.Element | null;
};

const createMediaUrl: CreateMediaUrl = ({ createMultiple, ...restArgs }) =>
  createMultiple
    ? `${resizeMediaUrl(restArgs)}, ${resizeMediaUrl({
        ...restArgs,
        requiredHeight: restArgs.requiredHeight * 2,
        requiredWidth: restArgs.requiredWidth * 2,
      })} 2x`
    : resizeMediaUrl(restArgs);

export const GalleryViewer: FC<GalleryViewerProps> = React.memo(
  ({
    componentData,
    nodeId,
    settings,
    relValue,
    anchorTarget,
    seoMode,
    helpers,
    itemOverlayElement,
  }) => {
    const { scrollingElement, ...gallerySettings } = settings;
    const galleryRef = useRef<HTMLDivElement>(null);
    const {
      items: ricosItems,
      options: ricosOptions,
      disableExpand,
      disableDownload,
    } = componentData || {};
    const { t } = useContext(RicosContext);
    const items = useItems(ricosItems || [], relValue, anchorTarget);
    const { options, calculatedOptions } = useOptions(ricosOptions || {}, disableDownload, items);

    const galleryOptions = {
      ...options,
      behaviourParams_item_overlay_backgroundColor: 'transparent',
    };

    const layout = ricosOptions?.layout?.type || '';

    const {
      setHeight,
      container,
      setContainerRef,
      containerStyle,
      containerClassName,
      isMeasuredContainer,
    } = useHeight({
      options: galleryOptions,
      layout,
    });

    const handleContextMenu = (e: $TSFixMe) => {
      return disableDownload && e.preventDefault();
    };
    const viewMode = seoMode ? GALLERY_CONSTS.viewMode.SEO : GALLERY_CONSTS.viewMode.SITE;

    const hasExpand =
      !disableExpand && !settings.disableExpand && settings.onExpand && ricosItems.length > 0;

    const handleExpand = (itemId: number) => {
      helpers?.onViewerAction?.(GALLERY_TYPE, 'Click', 'expand_gallery');
      hasExpand && settings.onExpand(nodeId, itemId);
    };

    const handleGalleryEvents = (name: $TSFixMe, data: $TSFixMe) => {
      switch (name) {
        case GALLERY_EVENTS.GALLERY_CHANGE:
          if (galleryRef.current) {
            if (!isHorizontalLayout(options)) {
              galleryRef.current.style.height = `${data.layoutHeight}px`;
            } else {
              galleryRef.current.style.height = 'auto';
            }
          }
          break;
        default:
          break;
      }
    };

    const customHoverRenderer = (itemProps: $TSFixMe) => {
      return (
        <div className={classes.itemOverlay}>
          {itemProps.title && (
            <div className={classes.imageTitleContainer}>
              <div className={classes.imageTitle}>{itemProps.title}</div>
            </div>
          )}
          {itemOverlayElement?.(itemProps)}
        </div>
      );
    };

    const isPrerenderMode = !isMeasuredContainer;

    const uniqueNodeId = useMemo(() => {
      const id = `${nodeId}-${settings.scopingId ?? 'not-scoped'}`;
      return isValidGalleryId(id) ? id : '';
    }, [nodeId]);

    const customImageRenderer = ({ alt, src, ...itemProps }: $TSFixMe) => {
      const srcProps = src.includes(', ') ? { src: src.split(', ')[0], srcSet: src } : { src };
      const item = items[itemProps?.['data-idx']];
      const isLink = item?.metaData.link;
      const isExpandable = item?.metaData.type === 'image' && !isLink && !disableExpand;

      return (
        <div className={classes.itemContainer}>
          <img
            {...itemProps}
            alt={alt}
            {...srcProps}
            {...(isExpandable
              ? {
                  onClick: () => {
                    handleExpand(itemProps['data-idx']);
                  },
                }
              : {})}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={isLink ? 0 : undefined}
          />
          {hasExpand && (
            <button
              className={classes.expandButton}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleExpand(itemProps['data-idx']);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleExpand(itemProps['data-idx']);
                }
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                }
              }}
              type="button"
              aria-label={t('Image_Expand_Button_AriaLabel')}
            >
              <ExpandIcon className={classes.expandIcon} />
            </button>
          )}
        </div>
      );
    };

    return (
      <div
        ref={setContainerRef}
        className={classNames(classes.gallery_container, containerClassName, {
          [classes.clickableItem]: hasExpand,
        })}
        style={containerStyle}
        data-hook={ROOT}
        role="none"
        onContextMenu={handleContextMenu}
      >
        <ProGallery
          id={uniqueNodeId}
          items={items.length > 0 ? items : sampleItems}
          options={galleryOptions}
          container={container}
          settings={gallerySettings as Settings}
          scrollingElement={
            typeof scrollingElement === 'function' ? scrollingElement() : scrollingElement
          }
          eventsListener={handleGalleryEvents}
          createMediaUrl={createMediaUrl}
          viewMode={viewMode}
          customComponents={{ customHoverRenderer, customImageRenderer }}
          totalItemsCount={items.length || sampleItems.length}
          // @ts-expect-error
          setLayoutHeightImp={setHeight}
          isPrerenderMode={isPrerenderMode}
        />
        {uniqueNodeId ? (
          <LayoutFixer
            id={uniqueNodeId}
            isPrerenderMode={isPrerenderMode}
            items={items}
            options={calculatedOptions}
          />
        ) : null}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return lodash.isEqual(
      filterOutNonDisplayedProps(prevProps),
      filterOutNonDisplayedProps(nextProps)
    );
  }
);

// These filtered-out fields cause rendering performance issues when typing in the inputs of settings modal
function filterOutNonDisplayedProps(props: GalleryViewerProps) {
  return {
    ...props,
    componentData: {
      ...props.componentData,
      items: props.componentData.items.map(
        ({ altText: _altText, title: _title, image, ...rest }) => {
          if (!image) {
            return rest;
          }
          const { link: _link, ...imageRest } = image;
          return { ...rest, image: imageRest };
        }
      ),
    },
  };
}
